// src/hooks/useEmailReport.tsx
import React, { useState, useCallback, useMemo } from "react";
import axios, { AxiosError } from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AppModal from "../components/Modal/AppModal";
import type { useToast } from "../components/ToastProvider/ToastProvider";
import type { Expense } from "../types/types";

export type EmailFormat = "pdf" | "csv" | "excel" | "json";

interface EmailDialogProps {
  emailDialogOpen: boolean;
  recipientEmail: string;
  emailError: string;
  emailFormat: EmailFormat;
  language: string;
  t: any;
  loading: boolean;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onFormatChange: (format: EmailFormat) => void;
  onSend: () => Promise<void>;
  setEmailError: (error: string) => void;
}

const FORMAT_OPTIONS: { value: EmailFormat; label: string }[] = [
  { value: 'pdf',   label: 'PDF Report' },
  { value: 'csv',   label: 'CSV Spreadsheet' },
  { value: 'excel', label: 'Excel' },
  { value: 'json',  label: 'JSON' },
];

const EmailDialogComponent = React.memo<EmailDialogProps>(({
  emailDialogOpen,
  recipientEmail,
  emailError,
  emailFormat,
  language: _language,
  t,
  loading,
  onClose,
  onEmailChange,
  onFormatChange,
  onSend,
  setEmailError,
}) => (
  <AppModal
    open={emailDialogOpen}
    onClose={onClose}
    title={t.emailReport.emailReport}
    maxWidth="sm"
    actions={{
      secondary: { label: t.emailReport.cancel, onClick: onClose },
      primary: {
        label: loading ? '' : t.emailReport.send,
        onClick: onSend,
        disabled: loading || !recipientEmail.trim(),
        startIcon: loading
          ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          : undefined,
      },
    }}
  >
    <div className="space-y-4 py-2">
      <div>
        <label className="block mb-1.5 text-sm font-medium text-[var(--text-color)]">
          {t.emailReport.recipientEmail} <span className="text-red">*</span>
        </label>
        <input
          type="email"
          placeholder={t.emailReport.example}
          value={recipientEmail}
          onChange={(e) => { onEmailChange(e.target.value); setEmailError(""); }}
          className="w-full px-3 py-2.5 rounded-input text-sm bg-white/5 border border-white/10 text-[var(--text-color)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-primary"
          style={{ border: emailError ? '2px solid #FF3B30' : undefined }}
          autoFocus
        />
        {emailError && <p className="text-red text-xs mt-1">{emailError}</p>}
      </div>

      <div>
        <p className="text-sm font-medium text-[var(--text-color)] mb-2">{t.emailReport.selectFormat}</p>
        <div className="space-y-2">
          {FORMAT_OPTIONS.map(({ value, label }) => (
            <label
              key={value}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                emailFormat === value
                  ? 'border-primary bg-primary/10'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${emailFormat === value ? 'border-primary' : 'border-white/30'}`}>
                {emailFormat === value && <div className="w-2 h-2 rounded-full bg-primary" />}
              </div>
              <input
                type="radio"
                name="emailFormat"
                value={value}
                checked={emailFormat === value}
                onChange={() => onFormatChange(value)}
                className="sr-only"
              />
              <span className={`text-sm ${emailFormat === value ? 'text-primary font-medium' : 'text-[var(--text-color)]'}`}>
                {label}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  </AppModal>
));

EmailDialogComponent.displayName = "EmailDialogComponent";

export interface IncomeSource {
  id: string;
  name: string;
  amount1?: number;
  amount2?: number;
  amount3?: number;
  amount4?: number;
  frequency: "weekly" | "biweekly" | "monthly" | "one-time";
  color?: string;
}

interface UseEmailReportParams {
  expenses: Expense[];
  waveAmounts: number[]; // [wave1, wave2, wave3, wave4] (unused entries can be 0)
  expenseListFrequency: "weekly" | "biweekly" | "monthly";
  incomeSources?: IncomeSource[]; // NEW: include additional income sources
  token: string | null;
  language: string;
  t: any;
  toast: ReturnType<typeof useToast>;
  getWaveLabel: (wave: number, frequency: string, t: any) => string;
  API_URL: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const useEmailReport = ({
  expenses,
  waveAmounts,
  expenseListFrequency,
  incomeSources = [],
  token,
  language,
  t,
  toast,
  getWaveLabel,
  API_URL,
  //loading,
  setLoading,
}: UseEmailReportParams) => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailFormat, setEmailFormat] = useState<EmailFormat>("pdf");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const waveCount = useMemo(() => {
    if (expenseListFrequency === "weekly") return 4;
    if (expenseListFrequency === "biweekly") return 2;
    return 1;
  }, [expenseListFrequency]);

  // totals per wave
  const waveTotals = useMemo(() => {
    return Array.from({ length: waveCount }, (_, i) => {
      const waveNum = i + 1;
      return expenses
        .filter((e) => (e.category === "Wave" || e.category === "Paycheck") && e.wave === waveNum)
        .reduce((sum, e) => sum + e.price, 0);
    });
  }, [expenses, waveCount]);

  const waveCounts = useMemo(() => {
    return Array.from({ length: waveCount }, (_, i) => {
      const waveNum = i + 1;
      return expenses.filter((e) => (e.category === "Wave" || e.category === "Paycheck") && e.wave === waveNum).length;
    });
  }, [expenses, waveCount]);

  const waveBalances = useMemo(() => waveTotals.map((total, i) => (waveAmounts[i] ?? 0) - total), [waveTotals, waveAmounts]);

  const totalWaveBudget = useMemo(() => waveAmounts.slice(0, waveCount).reduce((sum, a) => sum + (a ?? 0), 0), [waveAmounts, waveCount]);

  // include incomeSource totals per source (monthly calc based on frequency)
  const incomeSourcesSummary = useMemo(() => {
    return (incomeSources || []).map((src) => {
      const monthly = (src.frequency === "weekly")
        ? (src.amount1 ?? 0) + (src.amount2 ?? 0) + (src.amount3 ?? 0) + (src.amount4 ?? 0)
        : (src.frequency === "biweekly")
          ? (src.amount1 ?? 0) + (src.amount2 ?? 0)
          : (src.amount1 ?? 0); // monthly or one-time stored in amount1
      return {
        ...src,
        monthlyTotal: monthly,
      };
    });
  }, [incomeSources]);

  const generatePDFContent = useCallback(() => {
    // build income sources HTML
    const incomeHtml = incomeSourcesSummary.length
      ? `<h2>Income Sources</h2>
         <table>
           <tr><th>Source</th><th>Frequency</th><th>Wave amounts</th><th>Monthly Est.</th></tr>
           ${incomeSourcesSummary
             .map((src) => {
               const waves = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
                 .slice(0, (src.frequency === "weekly") ? 4 : (src.frequency === "biweekly") ? 2 : 1)
                 .map((a, i) => `W${i + 1}: $${a.toFixed(2)}`)
                 .join(" / ");
               return `<tr>
                 <td>${src.name}</td>
                 <td>${src.frequency}</td>
                 <td>${waves}</td>
                 <td>$${(src.monthlyTotal ?? 0).toFixed(2)}</td>
               </tr>`;
             })
             .join("")}
         </table>`
      : "";

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
            h1 { color: #315EFF; border-bottom: 3px solid #315EFF; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 30px; border-bottom: 2px solid #ddd; padding-bottom: 8px; }
            table { border-collapse: collapse; width: 100%; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 12px 8px; text-align: left; }
            th { background-color: #315EFF; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .paid { background-color: #d4edda !important; color: #155724; }
            .unpaid { background-color: #f8d7da !important; color: #721c24; }
            .summary-box { background-color: #f0f4ff; border: 2px solid #315EFF; border-radius: 8px; padding: 15px; margin: 20px 0; }
            .summary-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd; }
            .summary-item:last-child { border-bottom: none; }
            .label { font-weight: bold; }
            .value { color: #315EFF; }
            .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; }
            .positive { color: #28a745; }
            .negative { color: #dc3545; }
          </style>
        </head>
        <body>
          <h1>Budget Tracker Report</h1>
          <p><strong>Export Date:</strong> ${new Date().toLocaleString()}</p>

          <div class="summary-box">
            <h2>Overview Summary</h2>
            <div class="summary-item"><span class="label">Total Expenses:</span><span class="value">${expenses.length}</span></div>
            <div class="summary-item"><span class="label">Paid Expenses:</span><span class="value positive">${expenses.filter(e => e.isPaid).length}</span></div>
            <div class="summary-item"><span class="label">Unpaid Expenses:</span><span class="value negative">${expenses.filter(e => !e.isPaid).length}</span></div>
            <div class="summary-item"><span class="label">Total Amount:</span><span class="value">$${expenses.reduce((s, e) => s + e.price, 0).toFixed(2)}</span></div>
            ${waveAmounts.slice(0, waveCount).map((amt, i) => `<div class="summary-item"><span class="label">${getWaveLabel(i+1, expenseListFrequency, t)} Budget:</span><span class="value">$${(amt ?? 0).toFixed(2)}</span></div>`).join("")}
            <div class="summary-item"><span class="label">Total Wave Budget:</span><span class="value">$${totalWaveBudget.toFixed(2)}</span></div>
            <div class="summary-item"><span class="label">Frequency:</span><span class="value">${expenseListFrequency}</span></div>
          </div>

          ${incomeHtml}

          <h2>Wave/Paycheck Summary</h2>
          <table>
            <tr><th>Wave/Week</th><th>Budget</th><th>Expenses</th><th>Count</th><th>Balance</th></tr>
            ${Array.from({ length: waveCount }, (_, i) => `<tr>
              <td>${getWaveLabel(i+1, expenseListFrequency, t)}</td>
              <td>$${(waveAmounts[i] ?? 0).toFixed(2)}</td>
              <td>$${(waveTotals[i] ?? 0).toFixed(2)}</td>
              <td>${waveCounts[i]}</td>
              <td class="${(waveBalances[i] ?? 0) >= 0 ? "positive" : "negative"}">$${(waveBalances[i] ?? 0).toFixed(2)}</td>
            </tr>`).join("")}
          </table>

          <h2>All Expenses</h2>
          <table>
            <tr><th>Expense Name</th><th>Amount</th><th>Due Date</th><th>Category</th><th>Wave/Week</th><th>Status</th><th>Payment Type</th></tr>
            ${expenses.map(exp => `<tr class="${exp.isPaid ? "paid" : "unpaid"}">
              <td>${exp.name}</td>
              <td>$${exp.price.toFixed(2)}</td>
              <td>${exp.dueDate}</td>
              <td>${exp.category}</td>
              <td>${(exp.category === "Wave" || exp.category === "Paycheck") ? getWaveLabel(exp.wave!, expenseListFrequency, t) : "N/A"}</td>
              <td><strong>${exp.isPaid ? "Paid" : "Unpaid"}</strong></td>
              <td>${exp.paymentType ?? "N/A"}</td>
            </tr>`).join("")}
          </table>

          <div class="footer"><p>Generated by Budget Tracker | ${new Date().toLocaleDateString()}</p></div>
        </body>
      </html>
    `;
  }, [expenses, waveAmounts, waveCount, waveTotals, waveCounts, waveBalances, expenseListFrequency, getWaveLabel, t, incomeSourcesSummary, totalWaveBudget]);

  const sendEmailReport = useCallback(async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipientEmail.trim()) {
      setEmailError(t.emailReport.noEmail);
      return;
    }
    if (!emailRegex.test(recipientEmail)) {
      setEmailError(t.emailReport.invalidEmail);
      return;
    }

    setEmailError("");
    setLoading(true);

    try {
      let content = "";
      let filename = `budget-tracker-${new Date().toISOString().split("T")[0]}.${emailFormat}`;
      let isPdfBase64 = false;

      if (emailFormat === "pdf") {
        // Generate real PDF with jsPDF
        const doc = new jsPDF();
        doc.setFillColor(49, 94, 255);
        doc.rect(0, 0, doc.internal.pageSize.width, 28, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16).setFont("helvetica", "bold");
        doc.text("WaveBudget Tracker — Budget Report", 14, 18);
        doc.setTextColor(50, 50, 50).setFontSize(9).setFont("helvetica", "normal");
        doc.text(`Exported: ${new Date().toLocaleString()}   Frequency: ${expenseListFrequency}   Total Budget: $${totalWaveBudget.toFixed(2)}`, 14, 36);

        autoTable(doc, {
          startY: 44,
          head: [["Wave", "Budget", "Expenses", "Count", "Balance"]],
          body: Array.from({ length: waveCount }, (_, i) => [
            getWaveLabel(i + 1, expenseListFrequency, t),
            `$${(waveAmounts[i] ?? 0).toFixed(2)}`,
            `$${(waveTotals[i] ?? 0).toFixed(2)}`,
            String(waveCounts[i] ?? 0),
            `$${(waveBalances[i] ?? 0).toFixed(2)}`,
          ]),
          headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [245, 247, 250] },
          styles: { fontSize: 9, cellPadding: 3 },
        });

        if (incomeSourcesSummary.length > 0) {
          const y1 = (doc as any).lastAutoTable.finalY + 8;
          doc.setFontSize(11).setFont("helvetica", "bold").text("Income Sources", 14, y1);
          autoTable(doc, {
            startY: y1 + 4,
            head: [["Source", "Frequency", "Wave Amounts", "Monthly Est."]],
            body: incomeSourcesSummary.map((src) => {
              const count = src.frequency === "weekly" ? 4 : src.frequency === "biweekly" ? 2 : 1;
              const amounts = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
                .slice(0, count).map((a, i) => `W${i + 1}: $${a.toFixed(2)}`).join(" / ");
              return [src.name, src.frequency, amounts, `$${(src.monthlyTotal ?? 0).toFixed(2)}`];
            }),
            headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            styles: { fontSize: 9, cellPadding: 3 },
          });
        }

        const y2 = (doc as any).lastAutoTable.finalY + 8;
        doc.setFontSize(11).setFont("helvetica", "bold").text("All Expenses", 14, y2);
        autoTable(doc, {
          startY: y2 + 4,
          head: [["Name", "Amount", "Due Date", "Category", "Wave", "Status", "Payment"]],
          body: expenses.map((exp) => [
            exp.name,
            `$${exp.price.toFixed(2)}`,
            exp.dueDate ?? "—",
            exp.category,
            (exp.category === "Wave" || exp.category === "Paycheck") && exp.wave
              ? getWaveLabel(exp.wave, expenseListFrequency, t) : "N/A",
            exp.isPaid ? "Paid" : "Unpaid",
            exp.paymentType ?? "N/A",
          ]),
          headStyles: { fillColor: [49, 94, 255], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [245, 247, 250] },
          styles: { fontSize: 8, cellPadding: 3 },
          columnStyles: { 0: { cellWidth: 40 } },
        });

        // base64 encode the PDF output
        const raw = doc.output("arraybuffer");
        const bytes = new Uint8Array(raw);
        let binary = "";
        bytes.forEach((b) => { binary += String.fromCharCode(b); });
        content = btoa(binary);
        filename = `budget-tracker-${new Date().toISOString().split("T")[0]}.pdf`;
        isPdfBase64 = true;
      } else if (emailFormat === "excel") {
        content = generatePDFContent();
        filename = filename.replace(".excel", ".xls");
      } else if (emailFormat === "csv") {
        // CSV headers include income source summary and waves
        const headers = ["Expense Name","Amount","Due Date","Category","Wave/Week","Status","Payment Type","Payment URL"];
        const rows = expenses.map(exp => [
          exp.name,
          exp.price.toFixed(2),
          exp.dueDate,
          exp.category,
          exp.category === "Wave" || exp.category === "Paycheck" ? (exp.wave ?? "N/A") : "N/A",
          exp.isPaid ? "Paid" : "Unpaid",
          exp.paymentType ?? "N/A",
          exp.paymentUrl ?? "N/A",
        ]);
        content = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
      } else if (emailFormat === "json") {
        const exportData = {
          exportDate: new Date().toISOString(),
          settings: {
            waveAmounts: waveAmounts.slice(0, waveCount),
            frequency: expenseListFrequency,
            incomeSources,
            totalWaveBudget,
            language,
          },
          summary: {
            totalExpenses: expenses.length,
            paidExpenses: expenses.filter(e => e.isPaid).length,
            unpaidExpenses: expenses.filter(e => !e.isPaid).length,
            totalAmount: expenses.reduce((s, e) => s + e.price, 0),
            waveDetails: Array.from({ length: waveCount }, (_, i) => ({
              wave: i + 1,
              label: getWaveLabel(i + 1, expenseListFrequency, t),
              budget: waveAmounts[i] ?? 0,
              total: waveTotals[i] ?? 0,
              count: waveCounts[i],
              balance: waveBalances[i] ?? 0,
            })),
            incomeSources: incomeSourcesSummary,
          },
          expenses: expenses.map(exp => ({
            name: exp.name,
            amount: exp.price,
            dueDate: exp.dueDate,
            category: exp.category,
            wave: exp.wave,
            waveLabel: (exp.category === "Wave" || exp.category === "Paycheck") ? getWaveLabel(exp.wave!, expenseListFrequency, t) : null,
            isPaid: exp.isPaid,
            paymentType: exp.paymentType,
            paymentUrl: exp.paymentUrl,
          })),
        };
        content = JSON.stringify(exportData, null, 2);
      }

      const response = await axios.post(`${API_URL}/send-report-email`, {
        content, filename, format: emailFormat, recipientEmail: recipientEmail.trim(), isPdfBase64
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.status === 200) {
        setEmailDialogOpen(false);
        setRecipientEmail("");
        setEmailError("");
        toast.success(`${t.emailReport.reportSent} ${recipientEmail} ${t.emailReport.reportSentSuccess}`);
      }
    } catch (err) {
      const error = err as AxiosError<{ error: string }>;
      setEmailError(error.response?.data?.error || t.emailReport.failedToSend);
    } finally {
      setLoading(false);
    }
  }, [recipientEmail, emailFormat, expenses, language, token, API_URL, generatePDFContent, setLoading, waveAmounts, incomeSources, expenseListFrequency, totalWaveBudget, waveCount, waveTotals, waveCounts, waveBalances, t, incomeSourcesSummary]);

  return {
    emailDialogOpen,
    emailFormat,
    recipientEmail,
    emailError,
    setEmailDialogOpen,
    setEmailFormat,
    setRecipientEmail,
    setEmailError,
    sendEmailReport,
    EmailDialog: EmailDialogComponent,
  };
};
